import PreOrder from '../pre-order';
import * as _ from 'lodash';

export default class Cart {
  /**
   * @type {{}}
   */
  #cartApi;

  /**
   * @type {AppConfig}
   */
  #config;

  /**
   * @type {AppLibrary}
   */
  #library;

  /**
   * @type {{country_code:string}|null}
   */
  #geoData;

  /**
   * @type {boolean}
   */
  #fixCartBusy = false;

  /**
   * @type {boolean}
   */
  #fixCartQueued = false;

  /**
   * @type {function}
   */
  #onFixCartFinished = () => null;

  /**
   * @type {boolean}
   */
  #checkoutWarningShown = false;

  /**
   * @param {string} appName
   * @param {AppConfig} config
   * @param {AppLibrary} library
   * @param {{country_code:string}|null} geoData
   */
  constructor(appName, config, library, geoData) {
    this.#cartApi = Spurit.global.cartPool.getAdapter(appName);
    this.#config = config;
    this.#library = library;
    this.#geoData = geoData;
  }

  /**
   * @returns {{}}
   */
  get cartApi() {
    return this.#cartApi;
  }

  /**
   * @param {{id:number, quantity:number, properties:{}}[]} lineItems
   * @returns {{id:number, quantity:number, properties:{}}[]}
   */
  async fixLineItems(lineItems) {
    let lineItemsFixed = [];
    await Promise.all(lineItems.map(async lineItem => {
      let variantId = lineItem.id;
      let variant = this.#config.snippet().getVariantDetails(variantId);
      let settings = this.#config.getSettingsByVariantId(variantId);
      if (!variant || !settings || !PreOrder.isVariantAllowed(variant, settings, true)) {
        // Variant available without pre-order
        lineItemsFixed.push(lineItem);
        return;
      }
      if (!PreOrder.checkLimits(variant, settings, this.#config.settings().timezone)) {
        // Limits exceeded
        return;
      }

      const preOrderLabel = settings.config.preOrderLabel;

      // Split requested quantity to available for order and pre-order
      let {availableQuantity, availablePreOrder} = this.#getAvailableQuantity(
          variantId,
          preOrderLabel,
          lineItem.quantity,
          variant.inventory_quantity,
          settings.config.quantityLimit
      );

      if (availableQuantity === lineItem.quantity && !availablePreOrder) {
        // Variant available without pre-order
        lineItemsFixed.push(lineItem);
        return;
      }

      if (!await this.#addToCartConfirmation(lineItem.quantity, availableQuantity, availablePreOrder)) {
        // User cancelled adding current variant to cart
        return;
      }

      const similarCartItem = this.#getSimilarVariantFromCart(variantId, lineItem.properties, preOrderLabel);
      if (similarCartItem) {
        // Cart already contains the same line-item: add quantity/pre-ordered to new line-item and remove old line-item
        let po = this.#getPreorderedQuantityFromProperties(similarCartItem.properties, preOrderLabel);
        availablePreOrder += po;
        availableQuantity += similarCartItem.quantity - po;
        await new Promise(resolve => similarCartItem.remove(resolve));
      }

      // Add result to cart
      if (availablePreOrder && !PreOrder.checkLocation(this.#config.settings().config, this.#geoData)) {
        return;
      }
      lineItemsFixed.push({
        id: lineItem.id,
        quantity: availableQuantity + availablePreOrder,
        properties: {...lineItem.properties, ...(availablePreOrder ? {[preOrderLabel]: availablePreOrder} : {})}
      });
    }));

    return lineItemsFixed;
  }

  /**
   * @param {function} callback
   */
  onFixCartFinished(callback) {
    this.#onFixCartFinished = callback;
  }

  /**
   * @returns {boolean}
   */
  get fixCartBusy() {
    return this.#fixCartBusy;
  }

  /**
   * Returns number of fixed line-items
   * @returns {Promise<number>}
   */
  async fixCart() {
    if (!window.Spurit.global.multitabs.isTabActive()) {
      this.#fixCartQueued = false;
      return 0;
    }
    if (this.#fixCartBusy) {
      this.#fixCartQueued = true;
      return 0;
    }
    this.#fixCartBusy = true;

    // Collect and apply required cart updates
    let updates = await this.#getRequiredCartUpdates();
    updates.reverse();
    let appliedUpdates = await this.#applyCartUpdates(updates);
    console.log('Updates: ' + JSON.stringify(appliedUpdates));

    let updatesCount = appliedUpdates.length;
    this.#fixCartBusy = false;
    if (this.#fixCartQueued) {
      this.#fixCartQueued = false;
      updatesCount += await this.fixCart();
    }
    this.#onFixCartFinished();
    return updatesCount;
  }

  /**
   * @returns {number[]}
   */
  #getCartVariantIds() {
    let lineItems = this.#cartApi.getItems();
    let variantIds = [];
    lineItems.forEach(lineItem => !variantIds.includes(lineItem.variant_id) && variantIds.push(lineItem.variant_id));
    return variantIds;
  }

  /**
   * @returns {Promise<{key:string, total:number, preOrdered:number, preOrderLabel:string}[]>}
   */
  async #getRequiredCartUpdates() {
    let updates = [];
    await this.#library.asyncForEach(this.#getCartVariantIds(), async variantId => {
      let variant = this.#config.snippet().getVariantDetails(variantId);
      let settings = this.#config.getSettingsByVariantId(variantId);
      let preOrderLabel = settings.config.preOrderLabel;
      let variantUpdates = [];
      if (!variant || !PreOrder.isVariantAllowed(variant, settings, true)) {
        // Variant available without pre-order
        this.#getLineItemsByVariantId(variantId).forEach(lineItem => {
          variantUpdates.push({
            key: lineItem.key,
            total: lineItem.quantity,
            preOrdered: 0,
            preOrderLabel: preOrderLabel
          });
        });
      } else if (!PreOrder.checkLimits(variant, settings, this.#config.settings().timezone)/* || !PreOrder.checkLocation(this.#config.settings().config, this.#geoData)*/) {
        // Limits exceeded, remove line-items
        this.#getLineItemsByVariantId(variantId).forEach(lineItem => {
          variantUpdates.push({
            key: lineItem.key,
            total: lineItem.quantity,
            preOrdered: 0,
            preOrderLabel: preOrderLabel
          });
        });
      } else {
        // Variant available for pre-order
        variantUpdates = this.#getRequiredCartUpdatesForVariant(variantId, preOrderLabel, variant.inventory_quantity, settings.config.quantityLimit);
        if (!PreOrder.checkLocation(this.#config.settings().config, this.#geoData)) {
          variantUpdates.forEach(variantUpdate => variantUpdate.preOrdered = 0);
        }
      }
      updates = updates.concat(variantUpdates);
    });
    return updates;
  }

  /**
   * @param {number} variantId
   * @param {string} preOrderLabel
   * @param {number} inStockQuantity
   * @param {number} preOrderLimit
   * @returns {{key:string, total:number, preOrdered:number, preOrderLabel:string}[]}
   */
  #getRequiredCartUpdatesForVariant(variantId, preOrderLabel, inStockQuantity, preOrderLimit) {
    let {quantityTotal, quantityPreOrdered} = this.#getVariantQuantityFromCart(variantId, preOrderLabel);

    let setTotalTo = quantityTotal;
    let setPreOrderedTo = 0;
    if (quantityTotal > inStockQuantity) {
      // Part of items must be pre-ordered
      if (preOrderLimit) {
        // Pre-order quantity is limited
        if (inStockQuantity >= 0) {
          // in_stock >= 0
          setPreOrderedTo = quantityTotal - inStockQuantity;
          if (setPreOrderedTo > preOrderLimit) {
            setPreOrderedTo = preOrderLimit;
            setTotalTo = inStockQuantity + preOrderLimit;
          }
        } else {
          // in_stock < 0
          let maxAllowed = preOrderLimit - (-inStockQuantity);
          if (maxAllowed < 0) {
            maxAllowed = 0;
          }
          setTotalTo = Math.min(maxAllowed, quantityTotal);
          setPreOrderedTo = setTotalTo;
        }
      } else {
        // No pre-order limit
        setPreOrderedTo = quantityTotal;
        if (inStockQuantity > 0) {
          setPreOrderedTo -= inStockQuantity;
        }
      }
    }
    console.log(`Variant ${variantId}: ` + JSON.stringify({
      setTotalTo,
      quantityTotal,
      setPreOrderedTo,
      quantityPreOrdered
    }));
    if (setTotalTo === quantityTotal && setPreOrderedTo === quantityPreOrdered) {
      // Nothing to update
      return [];
    }

    // Collect required updates for each line-item
    let updates = [];
    let subsctractTotal = quantityTotal - setTotalTo;
    let subsctractPreOrdered = quantityPreOrdered - setPreOrderedTo;
    let lineItems = this.#getLineItemsByVariantId(variantId);
    lineItems.forEach(lineItem => {
      if (!subsctractTotal && !subsctractPreOrdered) {
        return;
      }
      let lineTotal = lineItem.quantity;
      let linePreOrdered = this.#getPreorderedQuantityFromProperties(lineItem.properties, preOrderLabel);
      let updatedTotal = Math.max(lineTotal - subsctractTotal, 0);
      let updatedPreOrdered = Math.min(
          Math.max(linePreOrdered - subsctractPreOrdered, 0),
          updatedTotal
      );
      subsctractTotal -= lineTotal - updatedTotal;
      subsctractPreOrdered -= linePreOrdered - updatedPreOrdered;
      updates.push({
        key: lineItem.key,
        total: updatedTotal,
        preOrdered: updatedPreOrdered,
        preOrderLabel: preOrderLabel
      });
    });
    return updates;
  }

  /**
   * @param {{key:string, total:number, preOrdered:number, preOrderLabel:string}[]} updates
   * @returns {Promise<{key:string, total:number, preOrdered:number, preOrderLabel:string}[]>}
   */
  async #applyCartUpdates(updates) {
    let lineItems = this.#cartApi.getItems();
    let appliedUpdates = [];
    await Promise.all(lineItems.map(async lineItem => {
      let update = updates.find(u => u.key === lineItem.key);
      if (!update) {
        return;
      }
      if (!update.total) {
        await new Promise(resolve => lineItem.remove(resolve));
        appliedUpdates.push(update);
        this.#showCheckoutWarning('Item was removed due to limits');
        return;
      }
      let linePreOrdered = this.#getPreorderedQuantityFromProperties(lineItem.properties, update.preOrderLabel);
      if (update.preOrdered !== linePreOrdered) {
        let totalOrig = lineItem.quantity;
        await new Promise(resolve => lineItem.remove(resolve));
        await new Promise(resolve => this.#cartApi.addItems(
            {
              id: lineItem.variant_id,
              quantity: update.total,
              properties: this.#updatePropertiesWithPreorder(lineItem.properties, update.preOrderLabel, update.preOrdered)
            },
            resolve
        ));
        appliedUpdates.push(update);
        if (!linePreOrdered && update.preOrdered) {
          this.#showCheckoutWarning('Item was not pre-ordered and now became so');
        }
        if (update.total !== totalOrig) {
          this.#showCheckoutWarning('Item total quantity was decreased due to limits (1)');
        }
        return;
      }
      if (update.total !== lineItem.quantity) {
        await new Promise(resolve => lineItem.setQuantity(update.total, resolve));
        appliedUpdates.push(update);
        this.#showCheckoutWarning('Item total quantity was decreased due to limits (2)');
      }
    }));
    return appliedUpdates;
  }

  /**
   * @param {string} reason
   */
  #showCheckoutWarning(reason) {
    if (this.#checkoutWarningShown) {
      return;
    }
    this.#checkoutWarningShown = true;
    console.log('Show checkout warning: ' + reason);
    alert(this.#config.settings().config.checkoutWarning);
  }

  /**
   * @param {number} variantId
   * @param {string} preOrderLabel
   * @param {number} requestedQuantity
   * @param {number} inventoryQuantity
   * @param {number} preOrderLimit
   * @returns {{availableQuantity: number, availablePreOrder: number}}
   */
  #getAvailableQuantity(variantId, preOrderLabel, requestedQuantity, inventoryQuantity, preOrderLimit) {
    let {quantityTotal, quantityPreOrdered} = this.#getVariantQuantityFromCart(variantId, preOrderLabel);
    inventoryQuantity = inventoryQuantity - (quantityTotal - quantityPreOrdered);
    let availableQuantity = inventoryQuantity > 0 ? Math.min(requestedQuantity, inventoryQuantity) : 0;
    let availablePreOrder = requestedQuantity - availableQuantity;
    if (preOrderLimit) {
      if (inventoryQuantity < 0) {
        preOrderLimit += inventoryQuantity;
      }
      preOrderLimit -= quantityPreOrdered;
      preOrderLimit = preOrderLimit > 0 ? preOrderLimit : 0;
      availablePreOrder = Math.min(availablePreOrder, preOrderLimit);
    }
    return {availableQuantity, availablePreOrder};
  }

  /**
   * @param {number} variantId
   * @returns {{quantity:number}[]}
   */
  #getLineItemsByVariantId(variantId) {
    return this.#cartApi.getItems().filter(cartItem => cartItem.variant_id === variantId);
  }

  /**
   * @param {number} variantId
   * @param {string} preOrderLabel
   * @returns {{quantityTotal: number, quantityPreOrdered: number}}
   */
  #getVariantQuantityFromCart(variantId, preOrderLabel) {
    let quantityTotal = 0;
    let quantityPreOrdered = 0;
    this.#getLineItemsByVariantId(variantId).forEach(cartItem => {
      quantityTotal += cartItem.quantity;
      quantityPreOrdered += this.#getPreorderedQuantityFromProperties(cartItem.properties, preOrderLabel);
    });
    return {quantityTotal, quantityPreOrdered};
  }

  /**
   * @param {Object.<string, string>|null} properties
   * @param {string} preOrderLabel
   * @returns {number}
   */
  #getPreorderedQuantityFromProperties(properties, preOrderLabel) {
    let result = (properties && properties[preOrderLabel]) || 0;
    return parseInt(result) || 0;
  }

  /**
   * @param {number} variantId
   * @param {Object.<string, string>|null} properties
   * @param {string} preOrderLabel
   * @returns {{}|null}
   */
  #getSimilarVariantFromCart(variantId, properties, preOrderLabel) {
    let canonizedProperties = this.#canonizeProperties(properties, preOrderLabel);
    return this.#cartApi.getItems().find(
        cartItem => cartItem.variant_id === variantId && canonizedProperties === this.#canonizeProperties(cartItem.properties, preOrderLabel)
    );
  }

  /**
   * @param {Object.<string, string>|null} properties
   * @param {string} exclude
   * @returns {string}
   */
  #canonizeProperties(properties, exclude = '') {
    if (!properties) {
      return '';
    }
    Object.entries(properties).filter(([key, value]) => key !== exclude).map(([key, value]) => `${key}:${value}`).sort().join(';');
  }

  /**
   * @param {Object.<string, string>|null} properties
   * @param {string} preOrderLabel
   * @param {number} preOrderQuantity
   * @returns {Object.<string, string>}
   */
  #updatePropertiesWithPreorder(properties, preOrderLabel, preOrderQuantity) {
    if (!properties) {
      return preOrderQuantity ? {[preOrderLabel]: preOrderQuantity} : {};
    }
    properties = _.clone(properties);
    if (preOrderQuantity) {
      properties[preOrderLabel] = preOrderQuantity;
    } else {
      delete (properties[preOrderLabel]);
    }
    return properties;
  }

  /**
   * @param {number} requestedQuantity
   * @param {number} availableQuantity
   * @param {number} availablePreOrder
   */
  async #addToCartConfirmation(requestedQuantity, availableQuantity, availablePreOrder) {
    let messages = [];
    if (availableQuantity && availablePreOrder) {
      messages.push(this.#config.settings().config.partiallyPreOrderWarning.replace(/:stock/g, availableQuantity));
    }
    if (availableQuantity + availablePreOrder < requestedQuantity) {
      messages.push(this.#config.settings().config.preOrderLimitWarning.replace(/:limit/g, availablePreOrder));
    }
    if (messages.length) {
      return window.confirm(messages.join("\n"));
    } else {
      return true;
    }
  }
}